import classNames from 'classnames';
import React, {
  Dispatch,
  FormEvent,
  MouseEvent,
  MouseEventHandler,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  SetStateAction,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button, Form } from 'react-bootstrap';
import { ButtonVariant } from 'react-bootstrap/esm/types';
import { FormattedMessage } from 'react-intl';

import { FormMessage, FormMsg } from 'components/forms/FormMessage';
import { Hint, HintProps } from 'components/hint/Hint';
import { capitalize } from 'helpers/helpers';
import { useModal } from './ModalContext';

import './Modal.scss';

// TODO: prevent scrolling in the underlying page,
// perhaps using the library body-scroll-lock.

type ModalHeaderProps = {
  title: ReactNode;
  right?: ReactNode;
  className?: string;
};
// ModalHeader: Renders the title and optional right-side actions
export function ModalHeader(props: ModalHeaderProps) {
  const { title, right, className } = props;
  return (
    <div className={`modal_header ${className ?? ''}`}>
      <div className="modal_title">{title}</div>
      {right && <div className="modal_header_right">{right}</div>}
    </div>
  );
}
ModalHeader.isModalHeader = true;

type ModalContentProps = {
  children: ReactNode;
  className?: string;
};
// ModalContent: Renders the main content of the dialog
export function ModalContent(props: ModalContentProps) {
  const { children, className } = props;
  // return <div className={`modal_content ${className ?? ''}`}>{children}</div>;
  return (
    <div className={`modal_content ${className ?? ''}`}>
      <div className="modal_content_inner">{children}</div>
    </div>
  );
}
ModalContent.isModalContent = true;

type ModalButtonProps = {
  onClick?: () => void | boolean | number | Promise<boolean | number>;
  noClose?: boolean;
  delayOnClose?: number;
  label?: ReactNode;
  variant?: ButtonVariant;
  outlined?: boolean;
  isDefault?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  absent?: boolean;
  setValidated?: Dispatch<SetStateAction<boolean>>;
  setValidForm?: Dispatch<SetStateAction<boolean>>;
  setFormMsg?: Dispatch<SetStateAction<FormMsg | undefined>>;
};
type btnType = 'ok' | 'cancel' | 'edit' | 'custom';
function defaultLabel(type: btnType) {
  if (type === 'custom') return null;
  return (
    <FormattedMessage
      id={`X.${capitalize(type, true)}`}
      defaultMessage={capitalize(type, type === 'ok')}
    />
  );
}
function singleDefault(...args: (ModalButtonProps | undefined)[]): void {
  let found = false;
  for (const arg of args) {
    if (!arg) continue;
    if (!found && arg.isDefault) found = true;
    else arg.isDefault = false;
  }
  if (!found) {
    const last = args[args.length - 1];
    if (last) last.isDefault = true;
  }
}
// ModalButton: Renders a button in the footer
export function ModalButton(
  props: ModalButtonProps &
    PropsWithChildren & {
      type: 'ok' | 'cancel' | 'edit' | 'custom';
    },
) {
  const {
    type,
    onClick,
    noClose = type === 'custom',
    delayOnClose,
    label,
    variant = 'secondary',
    outlined,
    isDefault,
    disabled,
    hidden,
    absent,
    setValidated,
    setValidForm,
    setFormMsg,
    children,
  } = props;
  const { closeModal } = useModal();
  const btnIsDefault = isDefault;
  const btnLabel = label ?? defaultLabel(type);

  // Store timeout id in a ref for cleanup
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
    };
  }, []);

  const close = useCallback(
    (val?: boolean | number) => {
      if (delayOnClose) {
        if (closeTimeout.current) clearTimeout(closeTimeout.current);
        closeTimeout.current = setTimeout(() => closeModal(val), delayOnClose);
      } else {
        closeModal(val);
      }
    },
    [closeModal, delayOnClose],
  );

  const validateForm = useCallback(
    (e: MouseEvent | FormEvent) => {
      // if we're not interested in validation, just return true
      if (!setValidated) return true;
      // get the form and validate, return false if form is not valid
      const form: HTMLFormElement | null =
        (e.currentTarget as HTMLButtonElement).form ||
        (e.currentTarget as HTMLFormElement);
      // check if form is still connected to DOM to avoid browser warnings
      if (form && !form.isConnected) return true; // skip validation if form is disconnected
      // if form is invalid, set validated to true and return false
      if (form && form.checkValidity() === false) {
        setValidated(true);
        setValidForm && setValidForm(false);
        e.stopPropagation();
        return false;
      }
      setValidForm && setValidForm(true);
      return true;
    },
    [setValidated, setValidForm],
  );

  const handleClick = useCallback(
    (e: MouseEvent | FormEvent) => {
      // Prevent any form submission
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;
      // if there is a form and it does not validate, do nothing
      if (!validateForm(e)) return;
      let canClose: boolean | number = !noClose;
      if (onClick) {
        try {
          const result = onClick();
          if (noClose) return;
          if (result instanceof Promise) {
            result
              .then((val) => {
                if (val) close(val);
              })
              .catch((reason) => {
                // Show error in modal if setFormMsg is provided
                if (setFormMsg) {
                  setFormMsg({
                    message:
                      (reason && reason.message) ||
                      (typeof reason === 'string'
                        ? reason
                        : 'An error occurred.'),
                    success: false,
                  });
                } else {
                  console.error('Form action failed.', reason);
                }
              });
            return;
          }
          canClose = result ?? false;
        } catch (reason) {
          if (setFormMsg) {
            setFormMsg({
              message:
                (reason && (reason as any).message) ||
                (typeof reason === 'string' ? reason : 'An error occurred.'),
              success: false,
            });
          } else {
            console.error('Form action failed.', reason);
          }
          return;
        }
      }
      if (canClose) close(canClose);
    },
    [onClick, disabled, noClose, close, setFormMsg, validateForm],
  );

  return absent ? null : (
    <Button
      variant={
        btnIsDefault ? 'primary' : (outlined ? 'outline-' : '') + variant
      }
      onClick={handleClick}
      disabled={disabled}
      className={classNames('modal_btn', 'modal_btn_cancel', {
        hidden: hidden,
      })}
      type={btnIsDefault && type !== 'cancel' ? 'submit' : 'button'}
    >
      {btnLabel || children}
    </Button>
  );
}

type ModalFooterProps = {
  okBtn?: ModalButtonProps;
  cancelBtn?: ModalButtonProps;
  editBtn?: ModalButtonProps;
  editing?: boolean;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
  className?: string;
  formMsg?: FormMsg;
  delayCloseOnSuccess?: number;
  setFormMsg?: Dispatch<SetStateAction<FormMsg | undefined>>;
};

type ValidationProps = {
  setValidated?: Dispatch<SetStateAction<boolean>>;
  setValidForm?: Dispatch<SetStateAction<boolean>>;
};
// ModalFooter: Renders the footer and optional left- and right-side actions
export function ModalFooter(
  props: ModalFooterProps & ValidationProps & PropsWithChildren,
) {
  const {
    okBtn,
    cancelBtn,
    editBtn,
    editing,
    footerLeft,
    footerRight,
    className,
    formMsg,
    setFormMsg,
    delayCloseOnSuccess,
    setValidated,
    setValidForm,
    children,
  } = props;
  // ensure only one button is default, in the given order
  singleDefault(cancelBtn, editBtn, okBtn);

  return (
    <>
      {formMsg ? <FormMessage {...formMsg} key={formMsg.key} /> : null}
      <div className={`modal_footer ${className ?? ''}`}>
        {footerLeft && <div className="modal_footer_left">{footerLeft}</div>}
        <div className="modal_footer_buttons">
          {cancelBtn ? (
            <ModalButton {...{ ...cancelBtn, type: 'cancel', setFormMsg }} />
          ) : null}
          {okBtn ? (
            <ModalButton
              {...{
                ...okBtn,
                type: 'ok',
                setValidated,
                setValidForm,
                setFormMsg,
                delayOnClose: delayCloseOnSuccess,
              }}
            />
          ) : null}
          {editBtn ? (
            <ModalButton
              {...{
                hidden: editing,
                noClose: true,
                ...editBtn,
                type: 'edit',
                setFormMsg,
              }}
            />
          ) : null}
        </div>
        {children}
        {footerRight && <div className="modal_footer_right">{footerRight}</div>}
      </div>
    </>
  );
}
ModalFooter.isModalFooter = true;

type ModalDialogProps = {
  onClose?: () => boolean;
  className?: string;
  title?: ReactNode;
  headerRight?: ReactNode;
  width?: 'small' | 'medium' | 'wide' | 'max' | number;
  height?: 'max' | number | string;
  enableEscape?: boolean;
  disableClose?: boolean;
  formMsg?: FormMsg;
  bare?: boolean;
  noFocusOnClose?: boolean;
  validated?: boolean;
  setValidForm?: Dispatch<SetStateAction<boolean>>;
};

// ModalDialog: Outer dialog container
export function ModalDialog(
  props: ModalDialogProps & ModalFooterProps & PropsWithChildren,
) {
  const {
    children,
    onClose,
    className,
    title = 'Dialog',
    headerRight,
    width = 'small',
    height,
    enableEscape = false,
    disableClose = false,
    formMsg,
    delayCloseOnSuccess,
    bare = false, // if true, only the content is shown, no other 'chrome'
    noFocusOnClose,
    setValidForm,
    editing,
  } = props;
  const shakeTimeout = useRef<NodeJS.Timeout | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const { closeModal } = useModal();
  const [validated, setValidated] = useState(false);

  // Find header/footer in children
  let header: ReactElement | null = null;
  let footer: ReactElement | null = null;
  const content: ReactNode[] = [];

  React.Children.forEach(children, (child) => {
    if (isValidElement(child) && (child.type as any).isModalHeader) {
      header = child;
    } else if (isValidElement(child) && (child.type as any).isModalFooter) {
      footer = child;
    } else {
      content.push(child);
    }
  });

  // shake the dialog
  const triggerShake = useCallback(() => {
    if (dialogRef.current) {
      dialogRef.current.classList.add('shake');
      if (shakeTimeout.current) clearTimeout(shakeTimeout.current);
      shakeTimeout.current = setTimeout(() => {
        dialogRef.current?.classList.remove('shake');
      }, 400);
    }
  }, []);

  // handle escape key
  useEffect(() => {
    // check if escape is enabled and if we're not editing
    // we don't respond to escape while editing -
    // to protect the user from accidentally cancelling
    // if (!enableEscape || editing) return;
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        if (!enableEscape || editing) {
          triggerShake();
          return;
        }
        e.stopPropagation(); // this is to prevent cancellation of menu clicks in the parent
        handleClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (shakeTimeout.current) clearTimeout(shakeTimeout.current);
      // Clean up shake class if still present
      const parent = dialogRef.current?.parentElement;
      if (parent) parent.classList.remove('shake');
    };
  }, [onClose, enableEscape, editing, triggerShake]);

  const handleClose = useCallback(() => {
    if (disableClose) {
      triggerShake();
      return;
    }
    const canClose = onClose ? onClose() : true;
    if (canClose) {
      // close the modal, asynchronously,
      // so any onClose effects can complete!
      closeModal(0);
      if (noFocusOnClose)
        setTimeout(() => {
          // remove focus from active element
          (document.activeElement as HTMLElement)?.blur();
        }, 0);
    } else triggerShake();
  }, [onClose, disableClose, triggerShake, closeModal, noFocusOnClose]);

  const footerProps = { ...props };
  delete footerProps.children;
  return (
    <div
      ref={dialogRef}
      className={classNames(
        'modal_dialog vertical_scrollable',
        typeof width !== 'number' ? `modal_dialog--${width}` : null,
        className,
        {
          maxHeight: height === 'max',
          bare,
          editing,
        },
      )}
      style={{
        width: typeof width === 'number' ? width : undefined,
        height: height !== 'max' ? height : undefined,
      }}
      onClick={bare ? handleClose : undefined}
    >
      <ModalForm isValidated={validated && (props.validated ?? true)}>
        {header ||
          (title ? <ModalHeader title={title} right={headerRight} /> : null)}
        {content.length === 1 && (content[0] as any).type?.isModalContent ? (
          content
        ) : (
          <ModalContent>{content}</ModalContent>
        )}
        {bare
          ? null
          : footer || (
              <ModalFooter
                {...footerProps}
                formMsg={formMsg}
                setValidated={setValidated}
                setValidForm={setValidForm}
                delayCloseOnSuccess={delayCloseOnSuccess}
              />
            )}
        {
          <button
            className="modal_close_btn"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        }
      </ModalForm>
    </div>
  );
}

type ModalFormProps = {
  className?: string;
  isValidated?: boolean | (() => boolean);
  onClick?: MouseEventHandler<HTMLFormElement>;
};

export function ModalForm(props: ModalFormProps & PropsWithChildren) {
  const { className, isValidated, onClick, children } = props;
  function validate() {
    if (typeof isValidated === 'function') return isValidated();
    return isValidated ?? false;
  }

  const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }, []);

  return (
    <Form
      noValidate
      validated={validate()}
      className={classNames('modal_form', className)}
      onClick={onClick}
      onSubmit={handleSubmit}
    >
      {children}
    </Form>
  );
}

type ModalDialogLinkProps<T> = {
  component: React.ComponentType<T>;
  componentProps?: T;
  className?: string;
  disabled?: boolean;
  hintProps?: HintProps;
  asButton?: boolean;
  closeLast?: boolean;
  closeAll?: boolean;
  inline?: boolean;
  inActive?: boolean;
  onClick?: () => void;
  onClose?: (success?: boolean) => void;
  style?: React.CSSProperties;
};

export function ModalDialogLink<T = unknown>(
  props: ModalDialogLinkProps<T> & PropsWithChildren,
) {
  const { openModal, closeModal, closeAllModals } = useModal();
  const {
    component: Component,
    componentProps,
    className,
    disabled,
    hintProps,
    asButton,
    closeLast,
    closeAll,
    inline,
    inActive,
    onClick,
    onClose,
    style,
    children,
  } = props;

  function handleClick() {
    onClick && onClick();
    if (closeAll) closeAllModals();
    else if (closeLast) closeModal();
    openModal(<Component {...(componentProps as any)} onClose={onClose} />);
  }

  function wrapHint(children: ReactNode) {
    if (hintProps) return <Hint {...hintProps}>{children}</Hint>;
    return children;
  }

  function wrapButton(children: ReactNode) {
    if (asButton)
      return (
        <Button variant="outline-secondary" className={className}>
          {children}
        </Button>
      );
    return children;
  }

  return inActive
    ? children
    : wrapHint(
        wrapButton(
          <div
            className={classNames('modal_dialog_link', className, {
              as_button: asButton,
            })}
            onClick={handleClick}
            style={{
              ...style,
              pointerEvents: disabled ? 'none' : undefined,
              display: inline ? 'inline' : undefined,
            }}
          >
            {children}
          </div>,
        ),
      );
}
